import { useEffect } from "react";
import formatDate from "utils/formateDate";

const TatGraphicalReportModal = ({
  isOpen,
  onClose,
  data = [],
  fromDate,
  toDate,
}) => {
  useEffect(() => {
    if (document.getElementById("jspdf-script")) return;
    const script = document.createElement("script");
    script.id = "jspdf-script";
    script.src =
      "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
    script.async = true;
    document.head.appendChild(script);
  }, []);

  useEffect(() => {
    if (!isOpen || data.length === 0) return;

    const generate = () => {
      if (!window.jspdf) {
        setTimeout(generate, 150);
        return;
      }

      const { jsPDF } = window.jspdf;

      const hasInvestigation = data.length > 0 && !!data[0].investigation_name;
      const hasDepartment = data.length > 0 && !!data[0].department_name;

      const totals = data.reduce(
        (acc, row) => {
          acc.inTat += row.test_in_tat || 0;
          acc.beyondTat += row.test_beyond_tat || 0;
          acc.notDefined += row.tat_not_defined || 0;
          acc.total += row.total_test || 0;
          return acc;
        },
        { inTat: 0, beyondTat: 0, notDefined: 0, total: 0 },
      );

      const doc = new jsPDF({ unit: "pt", format: "a4" });
      const pageW = doc.internal.pageSize.getWidth();
      const margin = 40;
      const contentW = pageW - margin * 2;
      let y = margin;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(14);
      doc.text(
        hasInvestigation
          ? "Investigation Wise TAT Report"
          : "Department Wise TAT Report",
        pageW / 2,
        y,
        { align: "center" },
      );
      y += 20;

      if (fromDate || toDate) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        doc.text(
          `From Date : ${formatDate(fromDate) || ""}   To Date : ${formatDate(toDate) || ""}`,
          pageW / 2,
          y,
          { align: "center" },
        );
        y += 20;
      }

      const cols = hasInvestigation
        ? [
            { label: "Sr No", w: 28 },
            { label: "Department", w: 98 },
            { label: "Investigation", w: 108 },
            { label: "Test In TAT", w: 68 },
            { label: "Test Beyond TAT", w: 82 },
            { label: "TAT Not Define", w: 73 },
            { label: "Total Test", w: 58 },
          ]
        : hasDepartment
          ? [
              { label: "Sr No", w: 40 },
              { label: "Department", w: 130 },
              { label: "Test In TAT", w: 80 },
              { label: "Test Beyond TAT", w: 100 },
              { label: "TAT Not Define", w: 90 },
              { label: "Total Test", w: 75 },
            ]
          : [
              { label: "Sr No", w: 55 },
              { label: "Test In TAT", w: 120 },
              { label: "Test Beyond TAT", w: 130 },
              { label: "TAT Not Define", w: 130 },
              { label: "Total Test", w: 80 },
            ];

      const rowH = 20;
      const headerFill = [217, 217, 217];
      const totalFill = [240, 240, 240];
      const oddFill = [245, 245, 245];
      const borderColor = [187, 187, 187];

      const drawRow = (cells, fillColor, bold = false) => {
        doc.setFillColor(...fillColor);
        doc.rect(margin, y, contentW, rowH, "F");

        doc.setDrawColor(...borderColor);
        doc.setLineWidth(0.5);

        doc.setFont("helvetica", bold ? "bold" : "normal");
        doc.setFontSize(9);
        doc.setTextColor(30, 30, 30);

        let x = margin;
        cells.forEach((cell, i) => {
          const cw = cols[i].w;
          doc.rect(x, y, cw, rowH);
          doc.text(String(cell ?? ""), x + cw / 2, y + rowH / 2 + 3, {
            align: "center",
          });
          x += cw;
        });
        y += rowH;
      };

      // Header row
      drawRow(
        cols.map((c) => c.label),
        headerFill,
        true,
      );

      // Data rows
      data.forEach((row, i) => {
        const cells = hasInvestigation
          ? [
              i + 1,
              row.department_name,
              row.investigation_name,
              row.test_in_tat,
              row.test_beyond_tat,
              row.tat_not_defined,
              row.total_test,
            ]
          : hasDepartment
            ? [
                i + 1,
                row.department_name,
                row.test_in_tat,
                row.test_beyond_tat,
                row.tat_not_defined,
                row.total_test,
              ]
            : [
                i + 1,
                row.test_in_tat,
                row.test_beyond_tat,
                row.tat_not_defined,
                row.total_test,
              ];

        drawRow(cells, i % 2 === 0 ? [255, 255, 255] : oddFill);
      });

      drawRow(
        hasInvestigation
          ? [
              "",
              "Total",
              "",
              totals.inTat,
              totals.beyondTat,
              totals.notDefined,
              totals.total,
            ]
          : hasDepartment
            ? [
                "",
                "Total",
                totals.inTat,
                totals.beyondTat,
                totals.notDefined,
                totals.total,
              ]
            : [
                "Total",
                totals.inTat,
                totals.beyondTat,
                totals.notDefined,
                totals.total,
              ],
        totalFill,
        true,
      );

      y += 20;

      // Pie chart heading
      doc.setFont("helvetica", "bold");
      doc.setFontSize(13);
      doc.setTextColor(30, 30, 30);
      doc.text("Graphical TAT", pageW / 2, y, { align: "center" });
      y += 16;

      // Pie chart
      const pieCanvas = document.createElement("canvas");
      const diameter = 220;
      pieCanvas.width = diameter;
      pieCanvas.height = diameter;
      const ctx = pieCanvas.getContext("2d");
      const cx = diameter / 2;
      const cy = diameter / 2;
      const radius = cx - 10;
      const pieTotal = totals.inTat + totals.beyondTat + totals.notDefined;

      if (pieTotal === 0) {
        ctx.beginPath();
        ctx.arc(cx, cy, radius, 0, 2 * Math.PI);
        ctx.fillStyle = "#e0e0e0";
        ctx.fill();
      } else {
        const segments = [
          { value: totals.inTat, color: "#4472C4" },
          { value: totals.beyondTat, color: "#ED7D31" },
          { value: totals.notDefined, color: "#70AD47" },
        ];
        let startAngle = -Math.PI / 2;
        segments.forEach(({ value, color }) => {
          if (value === 0) return;
          const slice = (value / pieTotal) * 2 * Math.PI;

          ctx.beginPath();
          ctx.moveTo(cx, cy);
          ctx.arc(cx, cy, radius, startAngle, startAngle + slice);
          ctx.closePath();
          ctx.fillStyle = color;
          ctx.fill();
          ctx.strokeStyle = "#fff";
          ctx.lineWidth = 2;
          ctx.stroke();

          const midAngle = startAngle + slice / 2;
          const labelR = radius * 0.62;
          const lx = cx + labelR * Math.cos(midAngle);
          const ly = cy + labelR * Math.sin(midAngle);
          const pct = ((value / pieTotal) * 100).toFixed(1);

          ctx.fillStyle = "#ffffff";
          ctx.font = "bold 13px helvetica";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillText(`${pct}%`, lx, ly - 8);
          ctx.font = "11px helvetica";
          ctx.fillText(`(${value})`, lx, ly + 9);

          startAngle += slice;
        });
      }

      const pieImgData = pieCanvas.toDataURL("image/png");
      const piePdfSize = 160;
      const pieX = (pageW - piePdfSize) / 2;
      doc.addImage(pieImgData, "PNG", pieX, y, piePdfSize, piePdfSize);
      y += piePdfSize + 16;

      // Legend
      const legendItems = [
        { color: [68, 114, 196], label: "#TAT In Time", value: totals.inTat },
        {
          color: [237, 125, 49],
          label: "#TAT Beyond Time",
          value: totals.beyondTat,
        },
        {
          color: [112, 173, 71],
          label: "#TAT Not Define",
          value: totals.notDefined,
        },
      ];
      const boxW = 200;
      const boxX = (pageW - boxW) / 2;
      const lineH = 18;
      const legendRows = legendItems.length * lineH + 20;
      const boxH = legendRows;

      doc.setDrawColor(...borderColor);
      doc.setLineWidth(0.7);
      doc.setFillColor(255, 255, 255);
      doc.rect(boxX, y, boxW, boxH, "FD");

      let ly = y + 12;
      const dotSize = 8;
      const dotX = boxX + 12;
      const labelX = dotX + dotSize + 6;
      const valX = boxX + boxW - 12;

      legendItems.forEach(({ color, label, value }) => {
        doc.setFillColor(...color);
        doc.rect(dotX, ly - dotSize + 2, dotSize, dotSize, "F");
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor(50, 50, 50);
        doc.text(label, labelX, ly);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(20, 20, 20);
        doc.text(String(value), valX, ly, { align: "right" });
        ly += lineH;
      });

      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(20, 20, 20);
      doc.text("Total:", labelX, ly);
      doc.text(String(totals.total), valX, ly, { align: "right" });

      const pdfBlob = doc.output("blob");
      const url = URL.createObjectURL(pdfBlob);
      window.open(url, "_blank");

      if (onClose) onClose();
    };

    generate();
  }, [isOpen]);

  return null;
};

export default TatGraphicalReportModal;
